import api from '@/api';
import { getOid, parseLocalizedString } from '@/services/utils';
import parsePhoneNumber from 'libphonenumber-js';
import { get, truncate as lodashTruncate } from 'lodash';
import _moment from 'moment';
import { physicalProductOrderStatusTextTranslation, productUnit } from '@/data/constants';

let dateFormat = 'YYYY/MM/DD (ddd)';
let datetimeFormat = 'YYYY/MM/DD (ddd) HH:mm';
let timeFormat = 'HH:mm';
let defaultDecimals = 2;
let defaultDollarSign = '$';

export { dateFormat, datetimeFormat, timeFormat, defaultDecimals, defaultDollarSign };

export function getId(v) {
  return getOid(v);
}

export function moment(v, format = datetimeFormat) {
  if (v == null) {
    return '';
  }
  return _moment(v).format(format);
}

export function calendarDate(v, longFormat = dateFormat, now = new Date()) {
  if (v == null) {
    return '';
  }
  const diff = +_moment(v).startOf('d').diff(_moment(now).startOf('d'), 'days');
  if (diff === 0) {
    return 'Today';
  } else if (diff > 0 && diff <= 1) {
    return 'Tomorrow';
  } else if (diff < 0 && diff >= -1) {
    return 'Yesterday';
  } else {
    return _moment(v).format(longFormat);
  }
}

export function duration(v, format) {
  if (v == null) {
    return '';
  }
  return _moment.duration(v).format(format);
}

export function datePrettify(v, longFormat = datetimeFormat, now = new Date()) {
  if (v == null) {
    return '';
  }
  v = _moment(v);
  return Math.abs(v.diff(now, 'day', true)) > 2 ? v.format(longFormat) : v.from(now);
}

export function time(v) {
  return moment(v, timeFormat);
}

function parseDateRange(v, startProp, endProp) {
  if (v == null) {
    return false;
  }
  const s = _moment(get(v, startProp, null));
  const e = _moment(get(v, endProp, null)).add(-1, 'ms');
  return {
    s: s.isValid() ? s : null,
    e: e.isValid() ? e : null,
  };
}

export function dateRange(v, startProp = 'start', endProp = 'end', pretty = true) {
  const { s, e } = parseDateRange(v, startProp, endProp);
  if (!s && !e) {
    return '';
  }
  const sD = s ? s.format(dateFormat) : '';
  const eD = e ? e.format(dateFormat) : '';
  if (pretty && sD === eD && sD) {
    return sD;
  }
  return `${sD} - ${eD}`;
}

export function timeRange(v, startProp = 'start', endProp = 'end') {
  const { s, e } = parseDateRange(v, startProp, endProp);
  if (!s && !e) {
    return '';
  }
  const sT = s ? s.format(timeFormat) : '';
  const eT = e ? e.format(timeFormat) : '';
  return `${sT} - ${eT}`;
}
export function timeDuration(v, startProp = 'start', endProp = 'end') {
  const { s, e } = parseDateRange(v, startProp, endProp);
  if (!s && !e) {
    return '';
  }
  //duration
  const duration = _moment.duration(_moment(e).diff(s));
  // duration in hours
  const hours = parseInt(duration.asHours());
  // duration in minutes
  const minutes = parseInt(duration.asMinutes()) % 60;

  if (hours || minutes) {
    return (hours ? `${hours}h` : '') + (minutes ? `${minutes}m` : '');
  }
  return '';
}

export function datetimeRange(v, startProp = 'start', endProp = 'end', pretty = true) {
  const { s, e } = parseDateRange(v, startProp, endProp);
  if (!s && !e) {
    return '';
  }
  const sD = s ? s.format(dateFormat) : '';
  const eD = e ? e.format(dateFormat) : '';
  // const sT = s ? s.format(timeFormat) : ''
  const eT = e ? e.format(timeFormat) : '';
  const sDT = s ? s.format(datetimeFormat) : '';
  const eDT = e ? e.format(datetimeFormat) : '';
  const exact = `${sDT} - ${eDT}`;
  let short = sD;
  if (sD !== eD) {
    short = exact;
  } else {
    short = `${sDT} - ${eT}`;
  }
  return pretty ? short : exact;
}

export function currency(
  v,
  dollar = defaultDollarSign,
  decimals = defaultDecimals,
  accountingFormat = false,
) {
  if (v == null) {
    return '';
  }
  const display =
    dollar +
    Math.abs(v)
      .toFixed(decimals)
      .replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
  if (accountingFormat) {
    return signed(v, '({v})', ' {v} ', undefined, display);
  }
  return signed(v, undefined, undefined, undefined, display);
}

export function amount(
  v,
  dollar = defaultDollarSign,
  decimals = defaultDecimals,
  accountingFormat = false,
) {
  if (v == null) {
    return '';
  }
  const prefix = v.currency
    ? Vue.i18n.translate(
        `currencies.${v.currency.toLowerCase()}.shortcode`,
        v.currency.toUpperCase() + dollar,
      )
    : '';
  return currency(v.value, prefix, decimals, accountingFormat);
}

/**
 * format a value with specific +ve/-ve/0 templates
 * @param {*} value numeric value
 * @param {string} negative template
 * @param {string} positive template
 * @param {string} zero template
 * @param {string|*} displayValue string value to substitute
 * @returns {string|*}
 */
export function signed(
  value,
  negative = '-{v}',
  positive = '{v}',
  zero = '{v}',
  displayValue = Math.abs(value),
) {
  const sign = parseFloat(value);
  if (isNaN(sign)) {
    return value;
  }
  const template = sign === 0 ? zero : sign < 0 ? negative : positive;
  return template.replace(/{v}/g, String(displayValue));
}

export function round(v, decimals = defaultDecimals) {
  if (v == null) {
    return '';
  }
  const order = Math.pow(10, decimals);
  return Math.round(order * v) / order;
}

export function siUnit(value, units, zeroIndex, base, decimals = defaultDecimals) {
  decimals = Math.pow(10, decimals);
  let order = Math.floor(Math.log(value) / Math.log(base));
  order = Math.min(Math.max(order + zeroIndex, 0), units.length - 1) - zeroIndex;
  const round = Math.round((value / Math.pow(base, order)) * decimals) / decimals;
  return `${round} ${units[order + zeroIndex]}`;
}

export function byte(value, decimals = defaultDecimals) {
  return siUnit(value, ['B', 'KB', 'MB', 'GB', 'TB'], 0, 1024, decimals);
}

export function objectId(v) {
  if (v == null) {
    return '';
  }
  return v.substr(-6).toUpperCase();
}

export function status(v) {
  if (v == null) {
    return '';
  }
  v = v.split('$').reverse()[0];
  // return Vue.i18n.translate('statuses.' + v, v[0].toUpperCase() + v.slice(1));
  return v[0].toUpperCase() + v.slice(1);
}

export function transBoolean(v) {
  if (v == null) {
    return '';
  }
  return v ? 'Yes' : 'No';
}

const availableSize = ['64', '128', '256', '512', 'hd', 'fhd', '2k'];

export function getThumbnail($axios, id, size = null) {
  return (
    $axios.defaults.baseURL +
    `/attachments/${getId(id)}/thumbnail${
      size && availableSize.includes(String(size)) ? `?size=${size}` : ''
    }`
  );
}

export function getFullQualityImage($axios, id) {
  return $axios.defaults.baseURL + `/attachments/${getId(id)}/download`;
}

export function capitalize(v) {
  if (!v) {
    return v;
  }
  v = String(v);
  return v.charAt(0).toUpperCase() + v.slice(1);
}

export function decapitalize(v) {
  if (!v) {
    return v;
  }
  v = String(v);
  return v.charAt(0).toLowerCase() + v.slice(1);
}

export function attnDownloadHref(id) {
  id = getId(id);
  const { host, base } = api.defaults;
  return `${host}${base}/attachments/${id}/download`;
}

export function attnThumbHref(id, size) {
  id = getId(id);
  size = size ? '?size=' + size : '';
  const { host, base } = api.defaults;
  return `${host}${base}/attachments/${id}/thumbnail/${size}`;
}

export function profileDisplayName(p, showPhone = false) {
  if (!p) return null;
  let phone = showPhone && displayPhone(p.phone);
  if (phone) {
    return [phone, p.name && `(${p.name})`].filter(x => x).join(' ');
  } else {
    return p.name;
  }
}

export function formatProfileName(profile) {
  if (!profile) return '';
  if (profile.role === 'rml.member') {
    const lastName = profile.name_last || '';
    const firstName = profile.name_first || '';
    if (lastName && firstName) {
      return `${lastName}, ${firstName}`;
    }
    return lastName || firstName || profile.name || '';
  }
  return profile.name || '';
}

export function displayPhone(
  v,
  formatter = x => {
    if (x.country === 'HK') return `+${x.countryCallingCode} ${x.formatNational()}`;
    return x.formatInternational();
  },
) {
  if (!v) return null;
  const parts = parsePhoneNumber(v) || parsePhoneNumber('+' + v);
  return parts ? formatter(parts) : v;
}

export function translateOptions(v, options) {
  if (!v) return null;
  if (!options) return null;
  let out;
  out = options.find(x => x.value === v);
  return out?.label || out?.text;
}

export function parseHKPhone(v) {
  return typeof v === 'string' ? v.replace('+852', '') : v;
}

/**
 * 10000 => "10,000"
 * @param {number} num
 */
export function toThousandFilter(num, decimals = null) {
  let value = +num || 0;
  value = decimals === null ? value.toString() : value.toFixed(decimals);
  return value.replace(/^-?\d+/g, m => m.replace(/(?=(?!\b)(\d{3})+$)/g, ','));
}

/**
 * 10000 => "10,000"
 * @param {string} transactionRef
 * @returns {string} -BookingId
 */
export function getBookingIdFromTransactionRef(transactionRef) {
  if (!transactionRef || !(typeof transactionRef === 'string')) return null;
  const x = transactionRef.split(':');
  return x?.[0] === 'BNBooking' ? x?.[1] : '-' || x?.[0] === 'BNTicketsPackage' ? x?.[1] : '-';
}

export function translatePhysicalProductOrderStatus(v) {
  const status = v.split('$')[1];
  const text = physicalProductOrderStatusTextTranslation[status];
  return text;
}
export function displayProductUnit(v) {
  return productUnit[v] ? productUnit[v].text : v;
}

export function truncate(v, length = 20) {
  return lodashTruncate(v, {
    length,
  });
}

export function parseAndLocalize(v, lang = 'en') {
  return parseLocalizedString(v)[lang];
}
